
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Search, Shield, UserCheck } from "lucide-react";

interface UserCredential {
  id: string;
  user_id: string;
  email: string;
  password_hash: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  user_type?: 'user' | 'client';
  profile?: {
    full_name: string | null;
    first_name: string | null;
    last_name: string | null;
  } | null;
}

interface UserSearchDialogProps {
  credentials: UserCredential[];
  isLoading: boolean;
  onSelectUser: (credential: UserCredential) => void;
}

export const UserSearchDialog = ({ credentials, isLoading, onSelectUser }: UserSearchDialogProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const getUserDisplayName = (credential: UserCredential) => {
    const profile = credential.profile;
    if (profile?.full_name) return profile.full_name;
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    if (profile?.first_name) return profile.first_name;
    return credential.email;
  };

  const handleUserSelect = (credential: UserCredential) => {
    setSearch("");
    setOpen(false);
    onSelectUser(credential);
  };

  // Filter out deleted/archived users and sort credentials alphabetically by display name
  const sortedCredentials = [...credentials]
    .filter(c => !c.email?.startsWith('deleted_') && !c.email?.startsWith('archived_'))
    .sort((a, b) => {
      const nameA = (getUserDisplayName(a) || '').toLowerCase();
      const nameB = (getUserDisplayName(b) || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

  let filteredCredentials = sortedCredentials;
  if (search) {
    const searchLower = search.toLowerCase();
    filteredCredentials = sortedCredentials.filter((credential) => {
      const displayName = getUserDisplayName(credential).toLowerCase();
      const email = credential.email?.toLowerCase() || "";
      const firstName = credential.profile?.first_name?.toLowerCase() || "";
      const lastName = credential.profile?.last_name?.toLowerCase() || "";
      
      return displayName.includes(searchLower) ||
             email.includes(searchLower) ||
             firstName.includes(searchLower) ||
             lastName.includes(searchLower);
    });
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/30 hover:border-primary/50 hover:bg-primary/10 transition-all duration-300"
        >
          <Search className="w-4 h-4 mr-2" />
          Quick Search
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] bg-gradient-to-br from-background to-muted/30 border-primary/20">
        <DialogHeader className="text-center pb-4">
          <DialogTitle className="text-xl font-bold flex items-center justify-center gap-2">
            <Search className="h-5 w-5 text-primary" />
            Search Users
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-background/50 border-primary/20 focus:border-primary/40 focus:ring-2 focus:ring-primary/20"
              placeholder="Enter name or email..."
              autoFocus
            />
          </div>
          
          <div className="text-sm text-muted-foreground">
            {isLoading ? "Loading users..." : `${filteredCredentials.length} user(s) found`}
          </div>
        </div>
        
        <ScrollArea className="h-[350px] w-full rounded-lg border border-primary/20 bg-background/50">
          {isLoading ? (
            <div className="p-6 text-center">
              <div className="flex flex-col items-center space-y-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="text-muted-foreground">Loading users...</p>
              </div>
            </div>
          ) : filteredCredentials.length === 0 ? (
            <div className="p-6 text-center">
              <div className="flex flex-col items-center space-y-3">
                <Search className="h-8 w-8 text-muted-foreground/50" />
                <div>
                  <p className="font-medium text-muted-foreground">No users found</p>
                  <p className="text-sm text-muted-foreground/70">Try a different search term</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-2">
              {filteredCredentials.map((credential, index) => (
                <button
                  key={credential.id}
                  className="flex items-center space-x-3 p-3 hover:bg-primary/10 w-full text-left rounded-lg transition-all duration-200 hover-scale group"
                  onClick={() => handleUserSelect(credential)}
                  style={{ animationDelay: `${index * 30}ms` }}
                >
                  <div className="relative">
                    <Avatar className="h-10 w-10 border-2 border-primary/20 group-hover:border-primary/40 transition-colors">
                      <AvatarImage src={`https://avatar.vercel.sh/${getUserDisplayName(credential)}.png`} />
                      <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/30 text-primary font-semibold">
                        {credential.profile?.first_name?.[0] || credential.email?.[0] || '?'}
                        {credential.profile?.last_name?.[0] || ""}
                      </AvatarFallback>
                    </Avatar>
                    <div className={`absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full border-2 border-background flex items-center justify-center ${
                      (credential.user_type || 'user') === 'client' 
                        ? 'bg-purple-500' 
                        : 'bg-green-500'
                    }`}>
                      {(credential.user_type || 'user') === 'client' ? (
                        <Shield className="h-2 w-2 text-white" />
                      ) : (
                        <UserCheck className="h-2 w-2 text-white" />
                      )}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-foreground group-hover:text-primary transition-colors">
                        {getUserDisplayName(credential)}
                      </p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        (credential.user_type || 'user') === 'client'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {(credential.user_type || 'user') === 'client' ? 'Client' : 'User'}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground truncate group-hover:text-muted-foreground/80 transition-colors">
                      {credential.email}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
